import {
  View,
  Heading,
  Text,
  Flex,
  Tooltip,
  TooltipTrigger,
  ActionButton
} from '@adobe/react-spectrum';
import React from 'react';
import { FormField as FormFieldType } from '../store/slice';
import { FormFieldComponent } from './FormField';
import styles from './CreateMoment.module.scss';
import ToolTipWrapper from '../../ToolTipWrapper/ToolTipWrapper';

interface FormSectionProps {
  sectionName: string;
  sectionConfig: {
    heading: string;
    subheading?: string;
    required?: boolean;
    columns?: number;
    toolTipMessage?: string;
  };
  fields: FormFieldType[];
  formValues: Record<string, any>;
  onChange: (name: string, value: any) => void;
  onBlur?: (name: string) => void;
  momentTypeId: string;
  validationErrors?: Record<string, string>;
  hasSubmitted?: boolean;
}

export const FormSection: React.FC<FormSectionProps> = ({
  sectionName,
  sectionConfig,
  fields,
  formValues,
  onChange,
  onBlur,
  momentTypeId,
  validationErrors = {},
  hasSubmitted = false
}) => {
  // Don't render if there are no fields
  if (fields.length === 0) {
    return null;
  }

  // Get the number of columns from sectionConfig, default to 1
  const columns = sectionConfig.columns || 1;

  // Section-level validation: if section is required and any field is ldap, require at least one to be filled
  const isPrimaryContactsSection = Boolean(
    sectionConfig.required && fields.some(f => f.type === 'ldap')
  );
  const anyContactSelected = isPrimaryContactsSection
    ? fields.some(
        f => Array.isArray(formValues[f.name]) && formValues[f.name]?.length > 0
      )
    : true;
  const sectionInvalid =
    isPrimaryContactsSection && hasSubmitted && !anyContactSelected;

  // Distribute fields into columns
  const distributeFields = () => {
    // Always use mixed-width layout builder so field-level overrides work even when section columns = 1

    // Mixed-width layout honoring per-field columns override
    // Determine the row grid units as the maximum of section.columns and any field-level columns.
    // This enables half-width fields even when the section default is 1 column.
    const maxFieldColumns = fields.reduce((max, f) => {
      const fc = (f as any).columns;
      return typeof fc === 'number' && fc > 0 ? Math.max(max, fc) : max;
    }, columns);

    const totalUnits = Math.max(columns, maxFieldColumns);

    type SpannedField = { field: FormFieldType; spanUnits: number };

    const spannedFields: SpannedField[] = fields.map(f => {
      const overrideColumns = (f as any).columns;
      const base =
        typeof overrideColumns === 'number' && overrideColumns > 0
          ? overrideColumns
          : columns;
      let spanUnits = Math.floor(totalUnits / base);
      if (!Number.isFinite(spanUnits) || spanUnits < 1) spanUnits = 1;
      if (spanUnits > totalUnits) spanUnits = totalUnits;
      return { field: f, spanUnits };
    });

    const rows: SpannedField[][] = [];
    let currentRow: SpannedField[] = [];
    let currentUnits = 0;

    spannedFields.forEach(sf => {
      const fieldUnits = Math.min(sf.spanUnits, totalUnits);
      // If this field doesn't fit in the current row, flush row and start new
      if (currentUnits + fieldUnits > totalUnits && currentUnits > 0) {
        rows.push(currentRow);
        currentRow = [];
        currentUnits = 0;
      }
      currentRow.push({ field: sf.field, spanUnits: fieldUnits });
      currentUnits += fieldUnits;
    });
    if (currentRow.length > 0) {
      rows.push(currentRow);
    }
    return (
      <View UNSAFE_className={styles.formSectionContent}>
        {rows.map((rowFields, rowIndex) => {
          const rowHidden = rowFields.every(
            rf => (rf.field as any)?.showField === false
          );
          const rowUnits = rowFields.reduce((sum, rf) => sum + rf.spanUnits, 0);
          const remainingUnits = Math.max(0, totalUnits - rowUnits);
          return (
            <Flex
              key={`row-${rowIndex}`}
              UNSAFE_className={`${styles.formSectionRow} ${
                rowHidden ? styles.formSectionRowHidden : ''
              }`}
              data-test-id={rowFields[0].field.id}
            >
              {rowFields.map((rf, colIndex) => (
                <View
                  UNSAFE_className={`${styles.formSectionFieldContainer} ${
                    (rf.field as any)?.showField === false
                      ? styles.formSectionFieldContainerHidden
                      : ''
                  }`}
                  key={`row-${rowIndex}-col-${colIndex}`}
                  flex={rf.spanUnits}
                >
                  <FormFieldComponent
                    key={`${momentTypeId}-${rf.field.id}`}
                    field={rf.field}
                    value={formValues[rf.field.name]}
                    onChange={value => onChange(rf.field.name, value)}
                    onBlur={onBlur}
                    formValues={formValues}
                    error={validationErrors[rf.field.name]}
                    hasSubmitted={hasSubmitted}
                    sectionInvalid={sectionInvalid && rf.field.type === 'ldap'}
                    toolTipMessage={rf.field.toolTipMessage}
                  />
                </View>
              ))}
              {remainingUnits > 0 && (
                <View
                  key={`row-${rowIndex}-empty`}
                  UNSAFE_className={styles.formSectionFieldContainer}
                  flex={remainingUnits}
                />
              )}
            </Flex>
          );
        })}
      </View>
    );
  };

  return (
    <View UNSAFE_className={styles.formSection}>
      {sectionConfig.heading != '' && (
        <View UNSAFE_className={styles.sectionHeaderContainer}>
          {/* <ToolTipWrapper message={sectionConfig.toolTipMessage}> */}
          <Heading level={3} UNSAFE_className={styles.sectionHeader}>
            {sectionConfig.heading}
            {sectionConfig.required && (
              <span className={styles.required}> *</span>
            )}
          </Heading>
          {/* </ToolTipWrapper> */}
          {sectionConfig.subheading && (
            <Text UNSAFE_className={styles.sectionSubheading}>
              {sectionConfig.subheading}
            </Text>
          )}
        </View>
      )}
      <View UNSAFE_className={styles.formFieldsContainer}>
        {distributeFields()}
      </View>
    </View>
  );
};
